import { createFaroLogMethods } from '@hello-aria/observability/client'

export const severityLevels = ['debug', 'info', 'warning', 'error', 'fatal'] as const
export type SeverityLevel = (typeof severityLevels)[number]

interface ErrorContext {
    tags?: Record<string, string>
    extra?: Record<string, unknown>
    level?: SeverityLevel
}

interface CaptureResult {
    eventId: string | null
    success: boolean
}

interface ErrorTrackingProvider {
    captureError(error: unknown, context?: ErrorContext): CaptureResult
    captureMessage(message: string, context?: ErrorContext): CaptureResult
    setUser(user: { id: string; email?: string } | null): void
    addBreadcrumb(message: string, data?: Record<string, unknown>): void
}

function normalizeError(error: unknown): Error {
    if (error instanceof Error) return error
    if (typeof error === 'string') return new Error(error)
    return new Error(JSON.stringify(error))
}

/**
 * Faro carries a flat string map as context, so tags, extra and level are merged into one
 * level of string values. Nested objects are JSON-stringified rather than dropped.
 */
function flattenContext(context?: ErrorContext): Record<string, string> {
    const flat: Record<string, string> = {}

    for (const [key, value] of Object.entries(context?.tags ?? {})) {
        flat[key] = value
    }

    for (const [key, value] of Object.entries(context?.extra ?? {})) {
        if (value === null || value === undefined) continue
        flat[key] = typeof value === 'string' ? value : JSON.stringify(value)
    }

    if (context?.level) {
        flat.level = context.level
    }

    return flat
}

/**
 * Reports through the Faro logging methods the app already had mounted.
 *
 * Errors arrive in Grafana as error-level log lines carrying the message and context, not as
 * exceptions, so there is no stack trace and no automatic grouping into issues.
 */
const faroProvider: ErrorTrackingProvider = {
    captureError(error, context) {
        try {
            createFaroLogMethods().logError(normalizeError(error).message, flattenContext(context))
            return { eventId: null, success: true }
        } catch {
            console.error('[ErrorTracking] Failed to capture error:', error)
            return { eventId: null, success: false }
        }
    },

    captureMessage(message, context) {
        try {
            const faroApi = createFaroLogMethods()
            const flat = flattenContext(context)

            if (context?.level === 'error' || context?.level === 'fatal') {
                faroApi.logError(message, flat)
            } else {
                faroApi.logWarning(message, flat)
            }

            return { eventId: null, success: true }
        } catch {
            console.error('[ErrorTracking] Failed to capture message:', message)
            return { eventId: null, success: false }
        }
    },

    setUser() {
        // Faro user identity is not wired up; identity is handled by Amplitude in this app.
    },

    addBreadcrumb(message, data) {
        createFaroLogMethods().logWarning(message, {
            ...flattenContext({ extra: data }),
            kind: 'breadcrumb'
        })
    }
}

let provider: ErrorTrackingProvider = faroProvider

export function setErrorTrackingProvider(newProvider: ErrorTrackingProvider) {
    provider = newProvider
}

export function captureError(error: unknown, context?: ErrorContext): CaptureResult {
    return provider.captureError(error, context)
}

export function captureMessage(message: string, context?: ErrorContext): CaptureResult {
    return provider.captureMessage(message, context)
}

export function addBreadcrumb(message: string, data?: Record<string, unknown>) {
    provider.addBreadcrumb(message, data)
}

export const SDD_ERROR_TAGS = {
    FLOW: 'sdd_mandate',
    ACTION: {
        SUBMIT: 'submit',
        VALIDATION: 'validation',
        FORM_VALIDATION: 'form_validation'
    }
} as const

export const SDD_ERROR_MESSAGES = {
    FORM_VALIDATION_FAILED: 'SDD mandate form validation failed',
    SDD_NOT_SUPPORTED: 'SDD not supported for this bank account',
    INVALID_IBAN: 'User submitted invalid IBAN'
} as const

export const SDD_POST_MESSAGE_ERRORS = {
    MANDATE_VALIDATION_FAILED: { code: 'MANDATE_VALIDATION_FAILED', message: 'The mandate validation step failed' },
    MANDATE_CREATION_FAILED: { code: 'MANDATE_CREATION_FAILED', message: 'Failed to create the SEPA mandate' },
    DEBTOR_FETCH_ERROR: { code: 'DEBTOR_FETCH_ERROR', message: 'Failed to load debtor information' }
} as const

export const INVOICE_VALIDATION_POST_MESSAGE_ERRORS = {
    INVOICE_FETCH_ERROR: { code: 'INVOICE_FETCH_ERROR', message: 'Failed to load invoice data' },
    INVOICE_ALREADY_VALIDATED: { code: 'INVOICE_ALREADY_VALIDATED', message: 'This invoice has already been validated' }
} as const

export type { ErrorContext, CaptureResult, ErrorTrackingProvider }
